import { motion } from "framer-motion";

const certifications = [
  {
    title: "Halal Certificate",
    issuer: "Halal Certification Authority",
    description: "Certified compliance with Halal manufacturing standards.",
    file: "/certifications/halal_certificate.pdf",
    filename: "PMS_Packages_Halal_Certificate.pdf",
    color: "from-green-500/10 to-emerald-500/5",
    iconColor: "text-green-600",
    badgeColor: "bg-green-100 text-green-700",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    title: "ISO 9001:2015 Certification",
    issuer: "International Organization for Standardization",
    description: "Quality management system certified to international ISO 9001:2015 standards.",
    file: "/certifications/ISO_9001_2015_CERTIFICATION.pdf",
    filename: "PMS_Packages_ISO_9001_2015_Certification.pdf",
    color: "from-blue-500/10 to-sky-500/5",
    iconColor: "text-blue-600",
    badgeColor: "bg-blue-100 text-blue-700",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
  },
  {
    title: "ISO 9001:2015 Surveillance",
    issuer: "International Organization for Standardization",
    description: "Surveillance audit confirming ongoing compliance with ISO 9001:2015.",
    file: "/certifications/ISO_9001_2015_SURVELLIANCE.pdf",
    filename: "PMS_Packages_ISO_9001_2015_Surveillance.pdf",
    color: "from-indigo-500/10 to-violet-500/5",
    iconColor: "text-indigo-600",
    badgeColor: "bg-indigo-100 text-indigo-700",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
  },
  {
    title: "FSC Chain of Custody",
    issuer: "Forest Stewardship Council",
    description: "Certified responsible sourcing of forest-based materials throughout the supply chain.",
    file: "/certifications/FSC_COC_Certificate.pdf",
    filename: "PMS_Packages_FSC_COC_Certificate.pdf",
    color: "from-teal-500/10 to-cyan-500/5",
    iconColor: "text-teal-600",
    badgeColor: "bg-teal-100 text-teal-700",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Certifications() {
  return (
    <section id="certifications" className="py-20 bg-gray-50 border-t border-gray-100">
      <div className="max-w-6xl mx-auto px-6">

        {/* Header */}
        <motion.div {...fadeUp(0)} className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block w-8 h-px bg-brand-green" />
            <span className="text-brand-green text-xs uppercase tracking-[0.2em] font-semibold">Accreditations</span>
            <span className="block w-8 h-px bg-brand-green" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl mb-3">Our Certifications</h2>
          <p className="text-gray-500 text-sm max-w-md mx-auto leading-relaxed">
            Independently verified quality and compliance credentials — available to download for your records.
          </p>
        </motion.div>

        {/* Cards — 2 cols on mobile, 4 cols on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {certifications.map((cert, i) => (
            <motion.div
              key={cert.title}
              {...fadeUp(0.1 + i * 0.1)}
              className={`relative group bg-gradient-to-br ${cert.color} bg-white rounded-2xl border border-gray-100 p-6 flex flex-col hover:shadow-md hover:border-gray-200 transition-all duration-300`}
            >
              {/* Icon */}
              <div className={`w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center mb-4 ${cert.iconColor}`}>
                {cert.icon}
              </div>

              {/* Badge */}
              <span className={`self-start text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full mb-3 ${cert.badgeColor}`}>
                Certified
              </span>

              <h3 className="font-heading text-base font-semibold mb-1 leading-snug">{cert.title}</h3>
              <p className="text-xs text-gray-400 mb-1">{cert.issuer}</p>
              <p className="text-sm text-gray-500 leading-relaxed mb-6 flex-1">{cert.description}</p>

              {/* Download button */}
              <a
                href={cert.file}
                download={cert.filename}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-brand-green hover:text-brand-green hover:bg-brand-green/5 transition-all duration-200 group-hover:shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download PDF
              </a>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}